// Admin (내부 관리자)
export interface Admin {
  id: number;
  email: string;
  name?: string;
  fullName?: string; // Backend returns fullName in camelCase
  full_name?: string; // Backend may also return full_name in snake_case
  role: 'admin' | 'manager';
  is_active?: boolean;
  last_login?: string;
}

// Helper to get display name for admin
export const getAdminDisplayName = (admin: Admin): string => {
  return admin.name || admin.fullName || admin.full_name || admin.email;
};

// User (외부 사용자 - Brand/Manufacturer)
export interface User {
  id: number;
  email: string;
  userType: 'brand' | 'manufacturer';
  company_name: string;
  contact_person: string;
  phone?: string;
  is_active: boolean;
  is_verified: boolean;
  last_login?: string;
  created_at: string;
  updated_at: string;
  // Brand 전용 필드들
  brand_id?: number;       // 브랜드 ID (id와 동일)
  brandId?: number;        // 브랜드 ID (camelCase)
  brand_code?: string;     // 브랜드 코드 (INC, SAL 등)
  // Manufacturer 전용 필드들
  code?: string;
  main_products?: string;
  address?: string;
  description?: string;
}

// Brand 타입 가드용 (기존 Brand 인터페이스 호환성)
export interface Brand extends User {
  userType: 'brand';
}

// 타입 가드 함수들
export const isAdmin = (user: Admin | User | null): user is Admin => {
  // Admin is identified by having role field (and NOT having userType which is for brand/manufacturer)
  return user !== null && 'role' in user && !('userType' in user);
};

export const isUser = (user: Admin | User | null): user is User => {
  return user !== null && 'contact_person' in user && 'userType' in user;
};

export const isBrand = (user: Admin | User | null): user is Brand => {
  return isUser(user) && user.userType === 'brand';
};

// 브랜드 사용자가 샘플 요청 가능한지 확인
// company_name과 contact_person이 필수
export const canSubmitSampleRequest = (user: Admin | User | null): boolean => {
  if (!isBrand(user)) return false;
  return !!(user.company_name && user.contact_person);
};

export const isManufacturer = (user: Admin | User | null): user is Manufacturer => {
  return isUser(user) && user.userType === 'manufacturer';
};

// Manufacturer 타입 가드용 (기존 Manufacturer 인터페이스 호환성)
export interface Manufacturer extends User {
  userType: 'manufacturer';
  main_products: string;
  code?: string;
  address?: string;
  description?: string;
}

// API Error Info (Phase 2 표준화된 에러 형식)
export interface ApiErrorInfo {
  code?: string;
  message: string;
  details?: Record<string, string>;
}

// API Response Types
export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
  error?: string | ApiErrorInfo;  // 문자열 또는 객체 형태 지원
  details?: any;
  timestamp?: string;
  // Backend-specific properties for different endpoints
  stats?: any;
  requests?: any[];
  status?: any;
  pagination?: {
    total: number;
    page: number;
    size?: number;
    limit?: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
    first?: boolean;
    last?: boolean;
  };
}

// 에러 메시지 추출 헬퍼 함수
export const getErrorMessage = (error: string | ApiErrorInfo | undefined): string => {
  if (!error) return 'An error occurred';
  if (typeof error === 'string') return error;
  return error.message || 'An error occurred';
};

export interface SampleRequestListResponse {
  success: boolean;
  message?: string;
  data?: SampleRequest[];
  error?: string;
  details?: any;
}

// 사용자 유형 상수 — 문자열 비교 hardcoding 대신 사용
export const USER_TYPE = {
  ADMIN: 'admin',
  BRAND: 'brand',
  MANUFACTURER: 'manufacturer',
} as const;
export type UserType = typeof USER_TYPE[keyof typeof USER_TYPE];

export const ADMIN_ROLE = {
  ADMIN: 'admin',
  MANAGER: 'manager',
} as const;
export type AdminRole = typeof ADMIN_ROLE[keyof typeof ADMIN_ROLE];

// Auth token data inside ApiResponse.data (Backend Jackson SNAKE_CASE)
export interface AuthTokenData {
  access_token: string;
  refresh_token?: string;
  user: Admin | User;
  user_type: UserType;
  admin_role?: AdminRole;
  // legacy camelCase aliases (deprecated — backend는 snake_case로 통일)
  accessToken?: string;
  refreshToken?: string;
  userType?: UserType;
  adminRole?: AdminRole;
}

// Full auth response from backend (ApiResponse<AuthTokenData>)
export interface AuthResponse {
  success: boolean;
  message?: string;
  data: AuthTokenData;
  error?: string;
}

// Sample Request Types
export interface ReferenceImage {
  url: string;
  storage_key?: string;
  type?: 'benchmark' | 'packaging';
  sort_order?: number;
}

export interface SampleRequest {
  id: number;
  request_code: string;
  company_name: string;
  contact_person: string;
  email: string;
  phone?: string;
  product_name: string;
  volume?: string;
  estimated_launch_quantity?: number;
  target_cost_budget?: number;
  target_market_country?: string;
  product_type?: string;
  efficacy_function?: string;
  formula_texture?: string;
  key_promotional_ingredients?: string;
  restricted_ingredients?: string;
  marketing_brand_claims?: string;
  fragrance?: string;
  color?: string;
  fragrance_color?: string; // Legacy field - to be deprecated
  primary_packaging?: string;
  secondary_packaging?: string;
  benchmark_product_name?: string;
  benchmark_product_link?: string;
  preferred_attributes?: string;
  non_preferred_attributes?: string;
  additional_requests_comments?: string;
  status: 'requested' | 'completed' | 'saved' | 'rejected' | 'cancelled';
  request_date?: string;
  created_at: string;
  updated_at: string;
  reference_packaging_image?: string;
  reference_images?: ReferenceImage[];
  admin_notes?: string;
  current_version?: number;
  project_id?: number;
  updated_fields?: string;
  brand_id?: number;
  is_latest?: boolean;
}

export interface SampleRequestForm {
  company_name: string;
  contact_person: string;
  email: string;
  phone?: string;
  product_name: string;
  volume?: string;
  estimated_launch_quantity?: number | null;
  target_cost_budget?: number | null;
  target_market_country?: string;
  product_type?: string;
  efficacy_function?: string;
  formula_texture?: string;
  key_promotional_ingredients?: string;
  restricted_ingredients?: string;
  marketing_brand_claims?: string;
  fragrance?: string;
  color?: string;
  fragrance_color?: string; // Legacy field - to be deprecated
  primary_packaging?: string;
  secondary_packaging?: string;
  benchmark_product_name?: string;
  benchmark_product_link?: string | null;
  preferred_attributes?: string;
  non_preferred_attributes?: string;
  additional_requests_comments?: string;
}


// Auth Context Types
export interface AuthState {
  user: Admin | User | null;
  accessToken: string | null;
  userType: 'admin' | 'brand' | 'manufacturer' | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

export interface AuthContextType extends AuthState {
  adminLogin: (email: string, password: string) => Promise<void>;
  sendVerificationCode: (email: string, purpose: string) => Promise<void>;
  verifyVerificationCode: (email: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
}


// Component Props Types
export interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  color?: string;
  className?: string;
  text?: string;
}

export interface LogoProps {
  size?: 'sm' | 'medium' | 'large' | 'xlarge';
  variant?: 'compact' | 'full';
  className?: string;
}

export interface ProtectedRouteProps {
  children: React.ReactNode;
  userType?: 'admin' | 'brand' | 'manufacturer';
}

export interface LayoutProps {
  children: React.ReactNode;
  showBackground?: boolean;
}

// Form Types
export interface LoginFormData {
  email: string;
  password: string;
}

export interface AdminMagicLinkForm {
  email: string;
}

export interface BrandForm {
  email: string;
  company_name?: string;
  contact_person?: string;
  password?: string;
}

// SampleRequestFormType alias for backward compatibility
export type SampleRequestFormType = SampleRequestForm;

// ========== OAuth Types ==========
export type OAuthProvider = 'google' | 'apple';

export interface OAuthRequest {
  provider: OAuthProvider;
  token: string;
  verificationCode?: string; // 신규 가입 시 브랜드 인증코드
  companyName?: string; // 신규 가입 시 회사명
  contactPerson?: string; // 신규 가입 시 담당자명
}

export interface OAuthUserInfo {
  provider: OAuthProvider;
  oauthId: string;
  email: string;
  name?: string;
  picture?: string;
  emailVerified: boolean;
}

// ========== Project Types ==========
export interface ProjectAdminInfo {
  id: number;
  name?: string;
  email: string;
  role?: string;
}

export interface Project {
  id: number;
  project_code?: string;
  brand_id: number;
  name: string;
  description?: string;
  target_market_country?: string;
  parent_id?: number;
  sort_order: number;
  is_active: boolean;
  status?: 'active' | 'completed' | 'archived';
  created_by?: number;
  created_at: string;
  updated_at: string;
  children?: Project[];
  sample_request_count?: number;
  assigned_admins?: ProjectAdminInfo[];
  assigned_admin_count?: number;
  /**
   * Get-or-Create 응답 시 신규 생성 여부.
   * - true: 이번 요청으로 새로 생성됨 (HTTP 201)
   * - false: 동일 이름의 기존 프로젝트가 반환됨 (HTTP 200)
   * - undefined: 일반 조회 응답에는 포함되지 않음
   */
  newly_created?: boolean;
}

export interface ProjectCreateRequest {
  name: string;
  description?: string;
  targetMarketCountry?: string;  // Market Distribution
  parent_id?: number;
}

export interface ProjectUpdateRequest {
  name?: string;
  description?: string;
  targetMarketCountry?: string;  // Market Distribution
  is_active?: boolean;
  assignedAdminIds?: number[];
}

export interface ProjectMoveRequest {
  project_id: number;
  new_parent_id?: number;
  new_sort_order?: number;
}

// ========== Batch Sample Request Types ==========
export interface BatchSampleRequestCreateRequest {
  project_id?: number;
  requests: SampleRequestForm[];
}

export interface BatchSampleRequestResponse {
  batch_group_id: number;
  requests: SampleRequest[];
}

// ========== Version History Types ==========
export interface SampleRequestVersion {
  id: number;
  sample_request_id: number;
  version: number;
  json_data: string;
  change_note?: string;
  created_by: string;
  created_by_type: 'admin' | 'brand';
  created_at: string;
}

export interface VersionData {
  productName: string;
  companyName: string;
  volume: string;
  productType: string;
  efficacyFunction: string;
  formulaTexture: string;
  keyPromotionalIngredients: string;
  restrictedIngredients: string;
  marketingBrandClaims: string;
  fragrance: string;
  color: string;
  primaryPackaging: string;
  secondaryPackaging: string;
  benchmarkProductName: string;
  status: string;
}

// ========== Admin Brand Management Types ==========
export interface AdminBrandCreateRequest {
  email: string;
  company_name: string;
  contact_person?: string;
  phone?: string;
  country_code?: string;
  verification_code?: string;
  brand_code?: string;
  initial_manager_email?: string;
  initial_manager_name?: string;
  is_active?: boolean;
  is_verified?: boolean;
}

export interface AdminBrandResponse {
  id: number;
  email: string;
  company_name: string;
  contact_person?: string;
  phone?: string;
  country_code?: string;
  verification_code?: string;
  brand_code?: string;
  is_verified: boolean;
  is_active: boolean;
  manager_count: number;
  last_login?: string;
  created_at: string;
  updated_at: string;
}

export interface BrandStats {
  total: number;
  active: number;
  verified: number;
  inactive: number;
}

// Spring Data Page 응답 (camelCase 직렬화 — 전역 SNAKE_CASE 설정 예외)
export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
  first: boolean;
  last: boolean;
}

// ========== Shipping Types ==========
export interface Shipping {
  id: number;
  sample_request_id: number;
  tracking_number: string;
  carrier: 'fedex' /*| 'ups' | 'dhl' | 'usps' | 'other'*/;
  status: 'pending' | 'in_transit' | 'delivered' | 'exception';
  estimated_delivery?: string;
  actual_delivery?: string;
  tracking_url?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}
// ===== Excel import dry-run 검증 (백엔드 ImportValidationResult, snake_case) =====
export interface ImportRowResult {
  row_number: number;
  fields: Record<string, string>;
  errors: string[];
  conflicts: string[];
  valid: boolean;
}

export interface ImportValidationResult {
  headers: string[];
  rows: ImportRowResult[];
  total: number;
  valid_count: number;
  invalid_count: number;
}

// ===== Sample Request 다중 브랜드 import 검증 (백엔드 SampleRequestImportValidation) =====
export interface ImportBrandGroup {
  key: string;
  company_name: string;
  email: string;
  row_count: number;
  matched_brand_id?: number | null;
  matched_brand_name?: string | null;
  matched_brand_email?: string | null;
  matched_by?: string | null;
}

export interface SampleRequestImportValidation {
  validation: ImportValidationResult;
  brand_groups: ImportBrandGroup[];
}
